// Centralized place to manage API calls

// Get the base URL injected by the build process
// It will be an empty string in development (to use relative paths for proxy)
// and the full backend URL in production.
const API_BASE_URL = import.meta.env.API_BASE_URL || '';

/**
 * Constructs the full API URL.
 * - In development (API_BASE_URL is empty), uses the relative path directly
 *   (e.g., '/api/users') so it hits the dev server proxy.
 * - In production, prepends the API_BASE_URL to the path.
 *
 * @param path - The API endpoint path (e.g., '/api/users', '/api/auth/login')
 * @returns The full API URL (for production) or relative path (for development)
 */
function getApiUrl(path: string): string {
  // Ensure path starts with a slash if it doesn't already
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  if (API_BASE_URL) {
    // Production: Prepend the base URL
    const cleanedBase = API_BASE_URL.endsWith('/')
      ? API_BASE_URL.slice(0, -1)
      : API_BASE_URL;
    return `${cleanedBase}${normalizedPath}`;
  } else {
    // Development: Use the relative path directly for the proxy
    // e.g., fetch('/api/auth/login') will go to http://localhost:3000/api/auth/login
    // and be proxied by rsbuild
    return normalizedPath;
  }
}

/**
 * A wrapper around the native fetch function that uses the appropriate
 * relative path (for dev proxy) or absolute URL (for production).
 *
 * @param path - The API endpoint path (e.g., '/api/auth/login')
 * @param options - Optional fetch options (method, headers, body, etc.)
 * @returns A Promise resolving to the Response object.
 */
export async function fetchApi(path: string, options?: RequestInit): Promise<Response> {
  const finalUrl = getApiUrl(path);
  console.log(`[fetchApi] Requesting: ${options?.method || 'GET'} ${finalUrl}`);
  try {
      // Fetch uses the finalUrl (relative for dev, absolute for prod)
      const response = await fetch(finalUrl, { ...options, credentials: 'include' });
      return response;
  } catch (error) {
      console.error(`[fetchApi] Network error for ${finalUrl}:`, error);
      throw error;
  }
}

// Example usage remains the same conceptually
// const response = await fetchApi('/api/auth/login', { ... }); 